/**
 * SafeAdminRedirect Component
 * 
 * Redirects /admin to the first accessible pinned route.
 * If user has no access, shows Access Denied with escape hatches.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCapabilities } from './CapabilityProvider';
import NotAuthorized from './NotAuthorized';
import NotAuthenticated from './NotAuthenticated';
import PermissionLoadError from './PermissionLoadError';
import AdminSpinner from './ui/AdminSpinner';
import DashboardPage from '../../pages/admin/DashboardPage';

// Pinned routes in order of preference (with their capability requirements)
const PINNED_ROUTES = [
  { path: '/admin/notifications', requires: null }, // Uses OR logic in page
  { path: '/admin/ops', requires: null }, // Uses OR logic in page
  { path: '/admin', requires: 'admin:integrations:read' }, // Dashboard requires this
];

export default function SafeAdminRedirect() {
  const navigate = useNavigate();
  const { loading, error, has, isUnauthenticated, isAuthenticated, me } = useCapabilities();
  
  // Debug logging (only when ?debug=1)
  const urlParams = new URLSearchParams(window.location.search);
  const isDebug = urlParams.get('debug') === '1';
  
  useEffect(() => {
    if (loading || error || isUnauthenticated) {
      return; // Wait for capabilities to load or show sign in
    }
    
    if (!isAuthenticated) {
      return; // Not authenticated, will show NotAuthenticated
    }
    
    // SUPER ADMIN BYPASS: If user is super admin, always show dashboard
    if (me?.isSuperAdmin === true) {
      if (isDebug) {
        console.log('[SafeAdminRedirect] Super admin bypass - showing dashboard directly');
      }
      return; // Don't redirect, show dashboard below
    }
    
    // Check if user has any of the common capabilities
    const hasAnyAccess = has('admin:integrations:read') || 
                        has('admin:audit:read') || 
                        has('admin:owner-dashboard:read');
    
    if (hasAnyAccess) {
      // User has some access, redirect to first pinned route
      // These routes use OR logic, so user should be able to access at least one
      navigate(PINNED_ROUTES[0].path, { replace: true });
    } else {
      // User has no access - stay on this page to show NotAuthorized
      // (component will render NotAuthorized below)
    }
  }, [loading, error, has, navigate, isUnauthenticated, isAuthenticated, me, isDebug]);
  
  if (loading) {
    return (
      <div style={styles.loading}>
        <AdminSpinner size="md" />
        <p style={{ marginTop: 'var(--spacing-2)', color: 'var(--color-text-tertiary)' }}>Loading...</p>
      </div>
    );
  }
  
  // If unauthenticated, show Sign In screen
  if (isUnauthenticated) {
    return <NotAuthenticated />;
  }
  
  if (error || !isAuthenticated) {
    return <PermissionLoadError />;
  }

  // SUPER ADMIN BYPASS: If user is super admin, always show dashboard
  if (me?.isSuperAdmin === true) {
    if (isDebug) {
      console.log('[SafeAdminRedirect] Super admin - showing dashboard');
    }
    return <DashboardPage />;
  }
  
  // Check if user has access to dashboard
  const canAccessDashboard = has('admin:integrations:read');
  
  if (canAccessDashboard) {
    // User can access dashboard, show it
    return <DashboardPage />;
  }
  
  // User cannot access dashboard, show access denied with escape hatches
  return (
    <NotAuthorized 
      requiredCapability="admin:integrations:read (or admin:audit:read OR admin:owner-dashboard:read for other pages)" 
    />
  );
}

const styles = {
  loading: {
    padding: 'var(--spacing-8)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-2)',
  },
};
